<template>
  <FilterBox>
    <div class="filter-box__header">
      <Button
        style-type="secondary"
        :disabled="!productsStore.hasActiveFilters"
        @click="resetAllFilters"
      >
        {{ $textDictionary['pdp.filters.resetFiltersNone.label'] }}
      </Button>
    </div>
    <div v-if="productsStore.products?.filters" class="filter-box__content">
      <AccordionComponent
        :key="componentKey"
        open-multiple-items
        :item-heading-level="4"
      >
        <template
          v-for="(filter, index) in productsStore.products?.filters"
          :key="filter.name"
        >
          <AccordionItem
            v-if="filter.values.length > 0"
            :is-open="!isEmpty(productsStore.filters[filter.name]) || index < 5"
            :data-testid="'productlist_filter_' + filter.name"
          >
            <template #title>
              {{
                $textDictionary[`productSearchFilter.${filter.name}.label`] ??
                `productSearchFilter.${filter.name}.label`
              }}
            </template>
            <!-- Use slider for range or price filters -->
            <div
              v-if="
                filter.name.includes('_range') ||
                filter.name.includes('price') ||
                filter.name === 'price'
              "
            >
              <Primevue2Slider
                :name="filter.name"
                :label="filter.name"
                :min="getMinValue(filter.values)"
                :max="getMaxValue(filter.values)"
                :possible-values="getNumericValues(filter.values)"
                :values="productsStore.filters[filter.name]"
                :min-label="
                  $textDictionary['productsList.filters.min.label'] ?? 'min'
                "
                :max-label="
                  $textDictionary['productsList.filters.max.label'] ?? 'max'
                "
                :currency-formatter="
                  filter.name.includes('price')
                    ? {
                        locale: $resolvedLocale?.toString() ?? 'en-US',
                        currency: 'EUR'
                      }
                    : undefined
                "
                :disabled="productsStore.filterDisabledList[filter.name]"
                @change="
                  (min, max) => handleSliderChange(filter.name, min, max)
                "
              />
            </div>
            <!-- Use checkboxes for other filters -->
            <div v-else>
              <!-- Search input field for filters with more than 8 options -->
              <div
                v-if="shouldShowSearch(filter.values)"
                class="form-item mb-3"
              >
                <PrimeVueFloatLabel variant="on">
                  <PrimeVueIconField>
                    <PrimeVueInputText
                      :id="`search_${filter.name}`"
                      v-model="facetQueries[filter.name]"
                      type="search"
                      name="search"
                      size="small"
                      suffix-icon="search"
                      :label="getSearchLabel(filter.name)"
                      :disabled="productsStore.filterDisabledList[filter.name]"
                      class="w-100 filter-search-input"
                      autocomplete="off"
                    />
                    <PrimeVueInputIcon>
                      <icon :name="'action_bar_magnifier'" />
                    </PrimeVueInputIcon>
                  </PrimeVueIconField>
                  <label :for="`search_${filter.name}`">{{
                    getSearchLabel(filter.name)
                  }}</label>
                </PrimeVueFloatLabel>
              </div>

              <div
                v-for="(value, valueIndex) in getFilteredValues(
                  filter.name,
                  filter.values
                )"
                :key="valueIndex"
                class="mb-2"
              >
                <div class="form-check">
                  <PrimeVueCheckbox
                    :binary="true"
                    :model-value="isFilterValueActive(filter.name, value.value)"
                    :input-id="`filter_${index}_${valueIndex}`"
                    :value="value.value"
                    :disabled="productsStore.filterDisabledList[filter.name]"
                    size="small"
                    @update:model-value="
                      () => handleCheckboxChange(filter.name, value.value)
                    "
                  />
                  <label :for="`filter_${index}_${valueIndex}`" class="p-label">
                    {{ `${normalizeFilterName(value.value)} (${value.count})` }}
                  </label>
                </div>
              </div>

              <!-- Show more/Show less button -->
              <LinkElement
                v-if="shouldShowMoreButton(filter.name, filter.values)"
                :link="''"
                :title="getButtonText(filter.name)"
                :target-blank="false"
                icon-type="icon-left"
                :icon-name="getChevronIcon(filter.name)"
                class="mt-3"
                :disabled="productsStore.filterDisabledList[filter.name]"
                @click="() => toggleShowMore(filter.name)"
              >
                {{ getButtonText(filter.name) }}
              </LinkElement>
            </div>
          </AccordionItem>
        </template>
      </AccordionComponent>
    </div>
  </FilterBox>
</template>

<script setup lang="ts">
import { useProductsStore } from '~/stores/productsStore/productsStore';
import {
  Button,
  FilterBox,
  AccordionComponent,
  AccordionItem,
  Icon,
  LinkElement
} from '@hypercodestudio/basler-components';
import { toggleValuesInList } from '~/stores/productFilterStore/helper/toggleValuesInList';
import Primevue2Slider from './Primevue2Slider.vue';
import { debounce } from '~/composables/useDebounce';

const { $textDictionary, $resolvedLocale } = useNuxtApp();
const productsStore = useProductsStore();

// Use a key to force component re-render based on filter state
const componentKey = computed(() => {
  return JSON.stringify(productsStore.filters);
});

// Get route reactively
const route = useRoute();

// Search functionality
const optionsToDisplay = 8;
const facetQueries = ref<Record<string, string>>({});
const facetSearchResults = ref<
  Record<string, Array<{ value: string; count: number }>>
>({});
const showLessStates = ref<Record<string, boolean>>({});

/**
 * Check if search should be shown for a filter
 */
function shouldShowSearch(
  filterValues: Array<{ value: string; count: number }>
): boolean {
  return filterValues.length > optionsToDisplay;
}

/**
 * Get search label for a filter
 */
function getSearchLabel(filterName: string): string {
  const filterLabel =
    $textDictionary.value[`productSearchFilter.${filterName}.label`] ??
    filterName;
  return (
    $textDictionary.value['productsList.filter.search.placeholder']?.replace(
      '{category}',
      filterLabel
    ) ?? `Search ${filterLabel}`
  );
}

/**
 * Get filtered values for a filter based on search query
 */
function getFilteredValues(
  filterName: string,
  filterValues: Array<{ value: string; count: number }>
): Array<{ value: string; count: number }> {
  const query = facetQueries.value[filterName];

  if (!query || !query.trim()) {
    // No search query, apply show more/show less logic
    const showLess = showLessStates.value[filterName] ?? true;
    if (showLess && filterValues.length > optionsToDisplay) {
      return filterValues.slice(0, optionsToDisplay);
    }
    return filterValues;
  }

  // Check if we have cached search results for this filter
  const searchResults = facetSearchResults.value[filterName];
  if (searchResults) {
    return searchResults;
  }

  return filterValues;
}

/**
 * Check if a filter has more options than the display limit
 */
function hasMoreOptions(
  filterValues: Array<{ value: string; count: number }>
): boolean {
  return filterValues.length > optionsToDisplay;
}

/**
 * Get the show more/show less button text for a filter
 */
function getButtonText(filterName: string): string {
  const showLess = showLessStates.value[filterName] ?? true;
  return showLess
    ? $textDictionary.value['productsList.filters.button.showmore.label'] ||
        'Show more'
    : $textDictionary.value['productsList.filters.button.showless.label'] ||
        'Show less';
}

/**
 * Get the chevron icon name for show more/show less button
 */
function getChevronIcon(filterName: string): string {
  const showLess = showLessStates.value[filterName] ?? true;
  return showLess ? 'chevron-down' : 'chevron-up';
}

/**
 * Toggle show more/show less state for a filter
 */
function toggleShowMore(filterName: string): void {
  const currentState = showLessStates.value[filterName] ?? true;
  showLessStates.value[filterName] = !currentState;
}

/**
 * Check if show more/show less button should be displayed
 */
function shouldShowMoreButton(
  filterName: string,
  filterValues: Array<{ value: string; count: number }>
): boolean {
  const query = facetQueries.value[filterName];
  // Only show the button if there's no active search and there are more options than the limit
  return (!query || !query.trim()) && hasMoreOptions(filterValues);
}

/**
 * Filter options based on search query
 */
function filterOptions(
  filterValues: Array<{ value: string; count: number }>,
  query: string
): Array<{ value: string; count: number }> {
  if (!query.trim()) {
    return filterValues;
  }

  return filterValues.filter((filterValue) => {
    const label = normalizeFilterName(filterValue.value);
    return label.toLowerCase().includes(query.toLowerCase());
  });
}

// Computed property to track filter states for reactivity
const filterStates = computed(() => {
  const states: Record<string, Record<string, boolean>> = {};

  // Process all available filters
  if (productsStore.products?.filters) {
    for (const filter of productsStore.products.filters) {
      states[filter.name] = {};

      // Get active filters from store first, then fallback to URL parameters
      let activeFilters = productsStore.filters[filter.name] || [];

      // If no active filters in store but URL has parameters, use URL parameters
      if (activeFilters.length === 0 && route.query[filter.name]) {
        const urlValue = route.query[filter.name];
        activeFilters = Array.isArray(urlValue)
          ? (urlValue.map((v) => v?.toString()).filter(Boolean) as string[])
          : urlValue
          ? [urlValue.toString()]
          : [];
      }

      for (const value of filter.values) {
        states[filter.name][value.value] = activeFilters.includes(value.value);
      }
    }
  }

  return states;
});

// Helper function to check if a filter value is active
const isFilterValueActive = (
  filterName: string,
  filterValue: string
): boolean => {
  return filterStates.value[filterName]?.[filterValue] || false;
};

const normalizeFilterName = (filterName: string) => {
  // some filters are named like this: 'Kamera /// anotherThing /// subItem
  // if that is the case we want the last part of the String. otherwise we return the whole filterName
  return filterName.split(' /// ').at(-1) ?? filterName;
};
/**
 * Helper function to check if an array is empty
 */
function isEmpty(arr: unknown[] | undefined | null): boolean {
  return !arr || arr.length === 0;
}

/**
 * Handle checkbox change by toggling the value in the filter array
 */
function handleCheckboxChange(filterName: string, value: string): void {
  const currentValues = productsStore.filters[filterName] || [];
  const newValues = toggleValuesInList(currentValues, value);
  productsStore.handleFormValueChange(filterName, newValues);
}

/**
 * Handle slider change by updating the filter with min and max values
 */
function handleSliderChange(
  filterName: string,
  min: number,
  max: number
): void {
  // Ensure min is less than or equal to max
  if (min > max) {
    // Swap values if min is greater than max
    const temp = min;
    min = max;
    max = temp;
  }

  productsStore.handleFormValueChange(filterName, [
    min.toString(),
    max.toString()
  ]);
}

/**
 * Get numeric values from filter values
 */
function getNumericValuesFromFilter(
  values: { value: string; count: number }[]
): number[] {
  if (!values || values.length === 0) {
    return [];
  }
  return values.map((v) => Number(v.value)).filter((v) => !isNaN(v));
}

/**
 * Get minimum value from filter values
 */
function getMinValue(values: { value: string; count: number }[]): number {
  const numericValues = getNumericValuesFromFilter(values);
  if (numericValues.length === 0) {
    return 0;
  }
  return Math.min(...numericValues);
}

/**
 * Get maximum value from filter values
 */
function getMaxValue(values: { value: string; count: number }[]): number {
  const numericValues = getNumericValuesFromFilter(values);
  if (numericValues.length === 0) {
    return 100;
  }

  // Get the max value
  const maxValue = Math.max(...numericValues);

  // Get the min value
  const minValue = Math.min(...numericValues);

  // Ensure max is greater than min
  return maxValue > minValue ? maxValue : minValue + 1;
}

/**
 * Get numeric values from filter values for the slider
 */
function getNumericValues(
  values: { value: string; count: number }[]
): number[] {
  const numericValues = getNumericValuesFromFilter(values);
  if (numericValues.length === 0) {
    return [0, 100];
  }
  return numericValues;
}

/**
 * Reset all filters, update query parameters, and force component re-render
 */
function resetAllFilters(): void {
  // Call the store's reset function which clears filters and updates the route
  productsStore.resetFilters();

  // Force immediate update and ensure query parameters are updated
  nextTick(() => {
    // Get the current route and router
    const route = useRoute();
    const router = useRouter();

    // If there are any filter-related query parameters still present, remove them
    if (Object.keys(route.query).length > 0) {
      // Keep only non-filter query parameters (like 'q', 'type', etc.)
      const nonFilterParams: Record<string, string | (string | null)[]> = {};
      ['q', 'type', 'page'].forEach((param) => {
        if (route.query[param]) {
          nonFilterParams[param] = route.query[param];
        }
      });

      // Update the route with only non-filter parameters
      router.replace({ query: nonFilterParams });
    }
  });
}

// Watch for changes in search queries with debounce
watch(
  facetQueries,
  debounce((newQueries: Record<string, string>) => {
    // Process each filter's search query
    Object.entries(newQueries).forEach(([filterName, query]) => {
      if (!query.trim()) {
        // Clear search results if query is empty
        delete facetSearchResults.value[filterName];
        return;
      }

      // Find the filter in productsStore
      const filter = productsStore.products?.filters?.find(
        (f) => f.name === filterName
      );
      if (filter) {
        // Filter the options based on the search query
        facetSearchResults.value[filterName] = filterOptions(
          filter.values,
          query
        );
      }
    });
  }, 300),
  { deep: true }
);
</script>

<style scoped>
.filter-box__header {
  min-height: 41px;
}

.p-label {
  cursor: pointer;
  margin-bottom: 0;
}

/* Search input styling to match design */
:deep(.filter-search-input) {
  border-radius: 4px;
}

:deep(.filter-search-input::placeholder) {
  color: #6c757d;
  opacity: 1;
}

:deep(.filter-search-input::-webkit-input-placeholder) {
  color: #6c757d;
  opacity: 1;
}

:deep(.filter-search-input::-moz-placeholder) {
  color: #6c757d;
  opacity: 1;
}

:deep(.filter-search-input:-ms-input-placeholder) {
  color: #6c757d;
  opacity: 1;
}

/* Ensure proper spacing between search and filter items */
.mb-3 + div:first-of-type {
  margin-top: 0.5rem;
}
</style>
